import { useLocalization } from "@fluent/react";
import { Button, Chip } from "@narmi/design_system";
import { useInstitution } from "byzantine/src/dbbl/businessLogic/entities";

import BalancesPlusDropdown from "../../../../components/dashboard/BalancesPlusDropdown";
import styles from "../AccountsModule.module.scss";

import ViewToggle from "./ViewToggle";

import type { ViewMode } from "../useViewPreference";
import type { FilterKey } from "./types";

export const FILTERS: { key: FilterKey; labelKey: string }[] = [
  { key: "all", labelKey: "dashboard-redesign-accounts-filter-all" },
  { key: "favorites", labelKey: "account-group-favorites" },
  { key: "checking", labelKey: "account-group-checking" },
  { key: "savings", labelKey: "account-group-savings" },
  { key: "credit_cards", labelKey: "account-group-credit-cards" },
  { key: "loans", labelKey: "account-group-loans" },
  { key: "cds", labelKey: "account-group-cds" },
];

type AccountsSectionHeaderProps = {
  title: string;
  view: ViewMode;
  onViewChange: (mode: ViewMode) => void;
  isEditing: boolean;
  onEditToggle: () => void;
  showPlusButton?: boolean;
  filter?: FilterKey;
  onFilterChange?: (f: FilterKey) => void;
  availableFilters?: FilterKey[];
  isSmall?: boolean;
  onTitleClick?: () => void;
};

const AccountsSectionHeader = ({
  title,
  view,
  onViewChange,
  isEditing,
  onEditToggle,
  showPlusButton = false,
  filter,
  onFilterChange,
  availableFilters,
  isSmall = false,
  onTitleClick,
}: AccountsSectionHeaderProps) => {
  const { l10n } = useLocalization();
  const institution = useInstitution();
  const hasEntryPoints =
    (institution?.account_opening_entry_points_config?.balances_dropdown ?? [])
      .length > 0;
  const visibleFilters = availableFilters
    ? FILTERS.filter(({ key }) => availableFilters.includes(key))
    : FILTERS;
  return (
    <div className={styles.sectionHeader}>
      <div className={styles.sectionHeaderLeft}>
        <div className={styles.sectionHeaderTitleRow}>
          {onTitleClick ? (
            <button
              type="button"
              className={`${styles.sectionHeaderTitle} ${styles.sectionHeaderTitleLink} button--reset`}
              onClick={onTitleClick}
            >
              {title}
              <span
                className={`narmi-icon-chevron-right ${styles.sectionHeaderTitleLinkIcon}`}
              />
            </button>
          ) : (
            <div className={styles.sectionHeaderTitle}>{title}</div>
          )}
          {!isSmall &&
            showPlusButton &&
            (hasEntryPoints ? <BalancesPlusDropdown /> : null)}
        </div>
        {!isSmall &&
          filter !== undefined &&
          onFilterChange &&
          !isEditing &&
          view !== "list" && (
            <div className={styles.filterChips}>
              {visibleFilters.map(({ key, labelKey }) => (
                <Chip
                  key={key}
                  label={l10n.getString(labelKey)}
                  kind={filter === key ? "primary" : "secondary"}
                  onClick={() => onFilterChange(filter === key ? "all" : key)}
                />
              ))}
            </div>
          )}
      </div>
      {!isSmall && (
        <div className={styles.sectionHeaderActions}>
          {view === "card" && (
            <Button
              kind="negative"
              size="s"
              label={
                isEditing
                  ? l10n.getString("dashboard-redesign-accounts-done")
                  : l10n.getString("dashboard-redesign-accounts-edit")
              }
              onClick={onEditToggle}
            />
          )}
          <ViewToggle view={view} onChange={onViewChange} />
        </div>
      )}
    </div>
  );
};

export default AccountsSectionHeader;
